import { TypedDocumentNode } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Mutation, MutationUpdateAccessGrantArgs, UpdateAccessGrantInput } from 'graphql/generated/graphqlTypes';
import { UPDATE_ACCESS_GRANT } from 'graphql/v2/mutations';
import { TranslatedHookProps } from '../hooks.types';

export type DayName = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const UPDATE_ACCESS_GRANT_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateAccessGrant'>,
  MutationUpdateAccessGrantArgs
> = UPDATE_ACCESS_GRANT;

const useSchedules = ({ translation, handleFetchError }: TranslatedHookProps) => {
  // The mutation returns the access grant's `id` and schedule, so every query that reads the grant (the groups) is
  // updated through the normalized cache and needs no refetch.
  const [updateAccessGrantMutation, { loading: updateAccessGrantMutationLoading }] = useMutation(
    UPDATE_ACCESS_GRANT_TYPED,
    {
      onError: () => handleFetchError(translation.error_while_updating_schedule)
    }
  );

  const updateAccessGrant = async (
    accessGrantId: string,
    input: UpdateAccessGrantInput
  ): Promise<{ success: boolean }> => {
    let mutationData;
    try {
      ({ data: mutationData } = await updateAccessGrantMutation({
        variables: { accessGrantId, input }
      }));
    } catch {
      // onError already shows the message, so don't show a second one.
      return { success: false };
    }
    if (!mutationData?.updateAccessGrant?.id) {
      handleFetchError(translation.error_while_updating_schedule);
      return { success: false };
    }
    return { success: true };
  };

  return {
    updateAccessGrant,
    updateAccessGrantMutationLoading
  };
};

export default useSchedules;
